import { useT } from "../../i18n/hooks.ts";
import { isSeriesScaleLocked, type ProjectSeries } from "../../model/dailySeries.ts";
import type { SeriesScale } from "../../model/seriesScale.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { InfoIcon } from "../InfoIcon.tsx";
import { SeriesLoader } from "../SeriesLoader.tsx";

/** Applies an entity update, folding in an inferred scale (from the FIRST series ever loaded into this project) when present — one project write, not two (see SeriesLoader.tsx's own `onEntitiesUpdated` doc comment). */
function applyProjectUpdate(
  project: EvalHidProject,
  onChangeProject: (project: EvalHidProject) => void,
  changes: Partial<EvalHidProject>,
  inferredScale?: SeriesScale
) {
  let p = { ...project, ...changes };
  if (inferredScale) p = { ...p, seriesScale: inferredScale };
  onChangeProject(p);
}

/** Step 5: input series — precipitation/ETP required, temperature/pumping optional. */
export function Step5Series({
  project,
  series,
  onChange,
  onChangeProject,
}: {
  project: EvalHidProject;
  series: ProjectSeries;
  onChange: (series: ProjectSeries) => void;
  onChangeProject: (project: EvalHidProject) => void;
}) {
  const t = useT();
  // Locked once ANY of the 4 series has real data — from then on, further
  // series must match that already-established scale. Before that, there's
  // no selector to set it: the scale is inferred from the actual spacing of
  // the first series' own dates (>=2 rows needed, see detectScale) the
  // moment it's loaded/pasted (see SeriesLoader.tsx's own `onFile`).
  const scaleLocked = isSeriesScaleLocked(series);
  return (
    <div>
      <h3>
        5. {t("panels.inputSeries")} <InfoIcon text={t("step5.help")} />
      </h3>
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.precipitation")}
        required
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.precipCol}
        withIndex={(c, index) => ({ ...c, precipCol: index })}
        series={series.precipitation}
        onLoaded={(s) => onChange({ ...series, precipitation: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.etp")}
        required
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.etpCol}
        withIndex={(c, index) => ({ ...c, etpCol: index })}
        series={series.etp}
        onLoaded={(s) => onChange({ ...series, etp: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.temperature")}
        required={false}
        entities={project.subBasins}
        entityPlural={t("topbar.subBasins")}
        nameOf={(c) => c.description}
        columnIndexOf={(c) => c.tempCol}
        withIndex={(c, index) => ({ ...c, tempCol: index })}
        series={series.temperature}
        onLoaded={(s) => onChange({ ...series, temperature: s })}
        onEntitiesUpdated={(subBasins, _sec, inferredScale) =>
          applyProjectUpdate(project, onChangeProject, { subBasins }, inferredScale)
        }
      />
      <SeriesLoader
        scale={project.seriesScale}
        scaleLocked={scaleLocked}
        label={t("infoPopover.pumping")}
        required={false}
        entities={project.aquifers}
        entityPlural={t("common.aquiferPlural")}
        nameOf={(a) => a.name}
        columnIndexOf={(a) => a.pumpingCol}
        withIndex={(a, index) => ({ ...a, pumpingCol: index })}
        series={series.pumping}
        onLoaded={(s) => onChange({ ...series, pumping: s })}
        // Both groups' freshly-resolved entities (and any inferred scale)
        // arrive together here (see SeriesLoader's own doc comment on why
        // this must be ONE call, not two/three separate onChangeProject
        // calls).
        onEntitiesUpdated={(aquifers, subBasins, inferredScale) =>
          applyProjectUpdate(
            project,
            onChangeProject,
            { aquifers, ...(subBasins ? { subBasins } : {}) },
            inferredScale
          )
        }
        // Ent_Bomb.csv links sub-basins by their OWN name too, independent
        // of any aquifer's own column — see SubBasin.pumpingCol's own doc
        // comment.
        secondaryGroup={{
          entities: project.subBasins,
          entityPlural: t("topbar.subBasins"),
          nameOf: (c) => c.description,
          columnIndexOf: (c) => c.pumpingCol,
          withIndex: (c, index) => ({ ...c, pumpingCol: index }),
        }}
      />
    </div>
  );
}
